<template>
  <div class="page-shell notifications-page">
    <section class="container">
      <section class="notifications-hero">
        <div v-if="isInitialLoading" class="notifications-hero__loading">
          <div class="hero-loading__main panel">
            <span class="skeleton-line skeleton-line--short"></span>
            <span class="skeleton-line skeleton-line--title"></span>
            <span class="skeleton-line skeleton-line--wide"></span>
            <div class="hero-loading__metrics">
              <div
                v-for="item in 3"
                :key="`hero-stat-${item}`"
                class="hero-metric hero-metric--skeleton"
              >
                <span class="skeleton-line skeleton-line--mini"></span>
                <span class="skeleton-line skeleton-line--stat"></span>
              </div>
            </div>
          </div>

          <div class="hero-loading__aside panel">
            <span class="skeleton-line skeleton-line--mini"></span>
            <span class="skeleton-line skeleton-line--card-title"></span>
            <span class="skeleton-line skeleton-line--wide"></span>
            <div class="hero-loading__actions">
              <span class="skeleton-chip"></span>
              <span class="skeleton-chip skeleton-chip--primary"></span>
            </div>
          </div>
        </div>

        <template v-else>
          <div class="notifications-hero__main">
            <div class="notifications-hero__intro">
              <p class="eyebrow">Message Deck</p>
              <h1 class="page-title">站内信</h1>
              <p class="page-description">
                把课程提醒、考试消息和系统通知集中到一个更清晰的工作台里。
              </p>
              <div class="notifications-hero__signal">
                <span class="notifications-hero__signal-dot"></span>
                <strong>{{ unreadCount > 0 ? '有新消息待处理' : '消息已经清空' }}</strong>
                <span>{{ unreadCount > 0 ? '建议先处理高优先级未读通知。' : '现在可以专注继续学习。' }}</span>
              </div>
            </div>

            <div class="notifications-hero__metrics">
              <article class="hero-metric">
                <span>未读消息</span>
                <strong>{{ unreadCount }}</strong>
                <small>{{ unreadCount > 0 ? '优先处理' : '状态良好' }}</small>
              </article>
              <article class="hero-metric">
                <span>当前页</span>
                <strong>{{ notifications.length }}</strong>
                <small>本页可见条目</small>
              </article>
              <article class="hero-metric">
                <span>累计消息</span>
                <strong>{{ total }}</strong>
                <small>按时间倒序排列</small>
              </article>
            </div>
          </div>

          <aside class="notifications-command">
            <div class="notifications-command__orbit notifications-command__orbit--lg"></div>
            <div class="notifications-command__orbit notifications-command__orbit--sm"></div>
            <div class="notifications-command__card">
              <p class="notifications-section-label">Message Ops</p>
              <h2>{{ unreadCount > 0 ? '整理一下收件箱' : '今天的通知已处理完成' }}</h2>
              <p class="notifications-command__copy">
                {{ unreadCount > 0 ? '先查看未读消息，再统一归档。' : '列表里仍可查看历史通知详情。' }}
              </p>

              <div class="notifications-command__meta">
                <div class="command-pill">
                  <span>页码</span>
                  <strong>{{ page }} / {{ totalPages }}</strong>
                </div>
                <div class="command-pill">
                  <span>状态</span>
                  <strong>{{ loading ? '同步中' : '已同步' }}</strong>
                </div>
              </div>

              <div class="notifications-command__actions">
                <button
                  class="btn btn--secondary btn--sm"
                  type="button"
                  :disabled="loading"
                  @click="loadNotifications(false)"
                >
                  刷新列表
                </button>
                <button
                  class="btn btn--primary btn--sm"
                  type="button"
                  :disabled="markingAll || unreadCount === 0"
                  @click="handleMarkAllRead"
                >
                  {{ markingAll ? '处理中...' : '全部标记已读' }}
                </button>
              </div>
            </div>
          </aside>
        </template>
      </section>

      <div v-if="errorMessage" class="status-card is-error notifications-status">
        {{ errorMessage }}
      </div>

      <template v-else>
        <div v-if="!isInitialLoading" class="notifications-tabs" role="tablist">
          <button
            v-for="item in tabOptions"
            :key="item.value"
            type="button"
            :class="['notifications-tab', { 'is-active': activeTab === item.value }]"
            @click="changeTab(item.value)"
          >
            {{ item.label }}
          </button>
        </div>

      <div v-if="isInitialLoading" class="notifications-workspace notifications-workspace--loading">
        <section class="panel notifications-sidebar">
          <div class="notifications-sidebar__header">
            <div>
              <span class="skeleton-line skeleton-line--mini"></span>
              <span class="skeleton-line skeleton-line--section-title"></span>
            </div>
            <span class="skeleton-badge"></span>
          </div>

          <div class="notifications-sidebar__toolbar notifications-sidebar__toolbar--skeleton">
            <span class="skeleton-line skeleton-line--wide"></span>
            <span class="skeleton-line skeleton-line--short"></span>
          </div>

          <div class="notifications-list">
            <article
              v-for="item in 6"
              :key="`list-skeleton-${item}`"
              class="notification-card notification-card--skeleton"
            >
              <div class="notification-card__avatar notification-card__avatar--skeleton"></div>
              <div class="notification-card__content">
                <div class="notification-card__topline">
                  <span class="skeleton-chip skeleton-chip--small"></span>
                  <span class="skeleton-line skeleton-line--mini"></span>
                </div>
                <span class="skeleton-line skeleton-line--card-title"></span>
                <span class="skeleton-line skeleton-line--wide"></span>
                <span class="skeleton-line skeleton-line--medium"></span>
                <div class="notification-card__footer">
                  <span class="skeleton-line skeleton-line--mini"></span>
                  <span class="skeleton-line skeleton-line--mini"></span>
                </div>
              </div>
            </article>
          </div>
        </section>

        <section class="panel notifications-viewer">
          <div class="notifications-viewer__header">
            <div>
              <span class="skeleton-line skeleton-line--mini"></span>
              <span class="skeleton-line skeleton-line--section-title"></span>
            </div>
            <span class="skeleton-badge"></span>
          </div>

          <div class="notification-detail notification-detail--skeleton">
            <div class="notification-detail__hero notification-detail__hero--skeleton">
              <div class="notification-detail__hero-copy">
                <span class="skeleton-chip skeleton-chip--small"></span>
                <span class="skeleton-line skeleton-line--title"></span>
                <span class="skeleton-line skeleton-line--wide"></span>
              </div>
              <div class="notification-detail__hero-aside">
                <span class="skeleton-line skeleton-line--mini"></span>
                <span class="skeleton-line skeleton-line--mini"></span>
                <span class="skeleton-line skeleton-line--mini"></span>
              </div>
            </div>

            <div class="notification-detail__body notification-detail__body--skeleton">
              <span class="skeleton-line skeleton-line--wide"></span>
              <span class="skeleton-line skeleton-line--wide"></span>
              <span class="skeleton-line skeleton-line--medium"></span>
              <span class="skeleton-line skeleton-line--wide"></span>
            </div>
          </div>
        </section>
      </div>

      <div v-else-if="activeTab === 'inbox'" class="notifications-workspace">
        <section class="panel notifications-sidebar">
          <div class="notifications-sidebar__header">
            <div>
              <p class="notifications-section-label">Inbox Queue</p>
              <h2>最近消息</h2>
            </div>
            <span class="notifications-badge">
              {{ unreadCount > 0 ? `${unreadCount} 未读` : '全部已读' }}
            </span>
          </div>

          <div class="notifications-sidebar__toolbar">
            <button
              v-for="item in readFilterOptions"
              :key="item.value"
              type="button"
              :class="['toolbar-chip', 'toolbar-chip--button', { 'is-active': readFilter === item.value }]"
              :disabled="loading"
              @click="changeReadFilter(item.value)"
            >
              <span>{{ item.label }}</span>
              <strong>{{ item.description }}</strong>
            </button>
            <div class="toolbar-chip">
              <span>分页</span>
              <strong>{{ page }} / {{ totalPages }}</strong>
            </div>
          </div>

          <div v-if="notifications.length === 0" class="notifications-empty notifications-empty--sidebar">
            <div class="notifications-empty__art">✉️</div>
            <strong>收件箱暂时是空的</strong>
            <p>新通知到达后会优先出现在这里。</p>
          </div>

          <div v-else class="notifications-list">
            <button
              v-for="item in notifications"
              :key="item.id"
              type="button"
              :class="['notification-card', {
                'is-active': selectedId === item.id,
                'is-unread': Number(item.is_read) === 0
              }]"
              @click="handleSelect(item)"
            >
              <div class="notification-card__avatar">
                {{ notificationInitial(item) }}
              </div>

              <div class="notification-card__content">
                <div class="notification-card__topline">
                  <span :class="['notification-chip', notificationTypeClass(item)]">
                    {{ item.type_word || '系统通知' }}
                  </span>
                  <span class="notification-card__time">
                    {{ formatNotificationTime(item.send_at || item.send_time || item.created_at) }}
                  </span>
                </div>

                <h3>{{ item.title || '未命名通知' }}</h3>
                <p>{{ item.content_preview || '暂无摘要内容' }}</p>

                <div class="notification-card__footer">
                  <span :class="['notification-state', { 'is-unread': Number(item.is_read) === 0 }]">
                    {{ item.is_read_word || (Number(item.is_read) === 1 ? '已读' : '未读') }}
                  </span>
                  <span class="notification-card__relative">
                    {{ formatRelativeTime(item.send_at || item.send_time || item.created_at) }}
                  </span>
                </div>
              </div>
            </button>
          </div>

          <div v-if="total > pageSize" class="notifications-pagination">
            <button
              class="btn btn--secondary btn--sm"
              :disabled="page === 1 || loading"
              @click="changePage(page - 1)"
            >
              上一页
            </button>
            <span class="pagination-info">第 {{ page }} / {{ totalPages }} 页</span>
            <button
              class="btn btn--secondary btn--sm"
              :disabled="page >= totalPages || loading"
              @click="changePage(page + 1)"
            >
              下一页
            </button>
          </div>
        </section>

        <section class="panel notifications-viewer">
          <div class="notifications-viewer__header">
            <div>
              <p class="notifications-section-label">Reading View</p>
              <h2>{{ activeNotification ? '消息详情' : '等待选择消息' }}</h2>
            </div>
            <span v-if="activeNotification" class="notifications-badge">
              {{ Number(activeNotification.is_read) === 1 ? '已读' : '未读' }}
            </span>
          </div>

          <div v-if="detailLoading" class="notification-detail notification-detail--skeleton">
            <div class="notification-detail__hero notification-detail__hero--skeleton">
              <div class="notification-detail__hero-copy">
                <span class="skeleton-chip skeleton-chip--small"></span>
                <span class="skeleton-line skeleton-line--title"></span>
                <span class="skeleton-line skeleton-line--wide"></span>
              </div>
              <div class="notification-detail__hero-aside">
                <span class="skeleton-line skeleton-line--mini"></span>
                <span class="skeleton-line skeleton-line--mini"></span>
                <span class="skeleton-line skeleton-line--mini"></span>
              </div>
            </div>

            <div class="notification-detail__body notification-detail__body--skeleton">
              <span class="skeleton-line skeleton-line--wide"></span>
              <span class="skeleton-line skeleton-line--wide"></span>
              <span class="skeleton-line skeleton-line--medium"></span>
              <span class="skeleton-line skeleton-line--wide"></span>
            </div>
          </div>

          <div v-else-if="detailError" class="status-card is-error notifications-status">
            {{ detailError }}
          </div>

          <div v-else-if="activeNotification" class="notification-detail">
            <div class="notification-detail__hero">
              <div class="notification-detail__hero-copy">
                <span :class="['notification-chip notification-chip--detail', notificationTypeClass(activeNotification)]">
                  {{ activeNotification.type_word || '系统通知' }}
                </span>
                <h3>{{ activeNotification.title || '未命名通知' }}</h3>
                <p class="notification-detail__lead">
                  {{ activeNotification.content_preview || '这条消息暂无摘要内容，请查看正文。' }}
                </p>
              </div>

              <div class="notification-detail__hero-aside">
                <article class="detail-stat">
                  <span>消息状态</span>
                  <strong>{{ Number(activeNotification.is_read) === 1 ? '已读消息' : '未读消息' }}</strong>
                </article>
                <article class="detail-stat">
                  <span>发送时间</span>
                  <strong>{{ formatNotificationTime(activeNotification.send_at || activeNotification.send_time || activeNotification.created_at) }}</strong>
                </article>
                <article class="detail-stat">
                  <span>阅读时间</span>
                  <strong>{{ activeNotification.read_at ? formatNotificationTime(activeNotification.read_at) : '尚未记录' }}</strong>
                </article>
              </div>
            </div>

            <div
              class="notification-detail__body"
              v-html="activeNotification.content || '<p>暂无内容</p>'"
            ></div>
          </div>

          <div v-else class="notifications-empty notifications-empty--viewer">
            <div class="notifications-empty__art">🗂️</div>
            <strong>选择一条消息查看详情</strong>
            <p>左侧列表会保留最近收到的课程通知和系统消息。</p>
          </div>
        </section>
      </div>

      <div v-else-if="activeTab === 'compose'" class="notifications-workspace">
        <section class="panel notifications-sidebar">
          <div class="notifications-sidebar__header">
            <div>
              <p class="notifications-section-label">Recipients</p>
              <h2>接收学员</h2>
            </div>
            <span class="notifications-badge">
              已选 {{ composeForm.recipientIds.length }}
            </span>
          </div>

          <div class="notifications-sidebar__toolbar">
            <label class="notification-field notification-field--compact">
              <span>搜索学员</span>
              <input
                v-model.trim="recipientKeyword"
                type="search"
                placeholder="姓名、账号或手机号"
                @keyup.enter="loadRecipientOptions"
              >
            </label>
            <button
              class="btn btn--secondary btn--sm"
              type="button"
              :disabled="recipientLoading"
              @click="loadRecipientOptions"
            >
              {{ recipientLoading ? '搜索中...' : '搜索' }}
            </button>
          </div>

          <div v-if="recipientOptions.length === 0" class="notifications-empty notifications-empty--sidebar">
            <strong>{{ recipientLoading ? '正在加载接收人' : '暂无可选学员' }}</strong>
            <p>可发送对象会出现在这里。</p>
          </div>

          <div v-else class="notifications-list">
            <button
              v-for="item in recipientOptions"
              :key="item.id"
              type="button"
              :class="['notification-card notification-card--recipient', { 'is-active': isRecipientSelected(item.id) }]"
              @click="toggleRecipient(item.id)"
            >
              <div class="notification-card__avatar">
                {{ notificationInitial({ title: item.name }) }}
              </div>
              <div class="notification-card__content">
                <div class="notification-card__topline">
                  <h3>{{ item.name }}</h3>
                  <span class="notification-state" :class="{ 'is-unread': isRecipientSelected(item.id) }">
                    {{ isRecipientSelected(item.id) ? '已选择' : '可选择' }}
                  </span>
                </div>
                <p>{{ item.courseName || item.mobile || '学员账号' }}</p>
              </div>
            </button>
          </div>
        </section>

        <section class="panel notifications-viewer">
          <div class="notifications-viewer__header">
            <div>
              <p class="notifications-section-label">Compose</p>
              <h2>发送定向通知</h2>
            </div>
            <span class="notifications-badge">
              站内信
            </span>
          </div>

          <form class="notification-compose" @submit.prevent="handleSendStudentNotification">
            <label class="notification-field">
              <span>通知标题</span>
              <input
                v-model.trim="composeForm.title"
                type="text"
                maxlength="80"
                placeholder="请输入通知标题"
              >
            </label>

            <label class="notification-field">
              <span>通知内容</span>
              <textarea
                v-model.trim="composeForm.content"
                rows="10"
                maxlength="2000"
                placeholder="请输入要发送给学员的内容"
              ></textarea>
            </label>

            <div class="notification-compose__selected">
              <span>接收人</span>
              <div v-if="selectedRecipientOptions.length" class="recipient-pills">
                <button
                  v-for="item in selectedRecipientOptions"
                  :key="`selected-${item.id}`"
                  type="button"
                  class="recipient-pill"
                  @click="toggleRecipient(item.id)"
                >
                  {{ item.name }}
                </button>
              </div>
              <p v-else>请从左侧选择至少一位学员。</p>
            </div>

            <div class="notification-compose__actions">
              <button
                class="btn btn--secondary"
                type="button"
                :disabled="sendingNotification"
                @click="composeForm = { title: '', content: '', recipientIds: [] }"
              >
                清空
              </button>
              <button
                class="btn btn--primary"
                type="submit"
                :disabled="sendingNotification"
              >
                {{ sendingNotification ? '发送中...' : '发送通知' }}
              </button>
            </div>
          </form>
        </section>
      </div>

      <div v-else class="notifications-workspace">
        <section class="panel notifications-sidebar">
          <div class="notifications-sidebar__header">
            <div>
              <p class="notifications-section-label">Sent</p>
              <h2>已发送</h2>
            </div>
            <span class="notifications-badge">
              {{ sentTotal }} 条
            </span>
          </div>

          <div class="notifications-sidebar__toolbar">
            <button
              class="btn btn--secondary btn--sm"
              type="button"
              :disabled="sentLoading"
              @click="loadSentNotifications(false)"
            >
              {{ sentLoading ? '同步中...' : '刷新已发送' }}
            </button>
            <div class="toolbar-chip">
              <span>分页</span>
              <strong>{{ sentPage }} / {{ sentTotalPages }}</strong>
            </div>
          </div>

          <div v-if="sentErrorMessage" class="status-card is-error notifications-status">
            {{ sentErrorMessage }}
          </div>

          <div v-else-if="sentNotifications.length === 0" class="notifications-empty notifications-empty--sidebar">
            <strong>{{ sentLoading ? '正在加载已发送通知' : '还没有发送记录' }}</strong>
            <p>从写通知发出的消息会保留在这里。</p>
          </div>

          <div v-else class="notifications-list">
            <button
              v-for="item in sentNotifications"
              :key="item.id"
              type="button"
              :class="['notification-card', { 'is-active': selectedSentId === item.id }]"
              @click="handleSelectSent(item)"
            >
              <div class="notification-card__avatar">
                {{ notificationInitial(item) }}
              </div>
              <div class="notification-card__content">
                <div class="notification-card__topline">
                  <span :class="['notification-chip', notificationTypeClass(item)]">
                    {{ item.type_word || '学员通知' }}
                  </span>
                  <span class="notification-card__time">
                    {{ formatNotificationTime(item.send_at || item.created_at) }}
                  </span>
                </div>
                <h3>{{ item.title || '未命名通知' }}</h3>
                <p>{{ item.content_preview || '暂无摘要内容' }}</p>
              </div>
            </button>
          </div>

          <div v-if="sentTotal > sentPageSize" class="notifications-pagination">
            <button
              class="btn btn--secondary btn--sm"
              :disabled="sentPage === 1 || sentLoading"
              @click="changeSentPage(sentPage - 1)"
            >
              上一页
            </button>
            <span class="pagination-info">第 {{ sentPage }} / {{ sentTotalPages }} 页</span>
            <button
              class="btn btn--secondary btn--sm"
              :disabled="sentPage >= sentTotalPages || sentLoading"
              @click="changeSentPage(sentPage + 1)"
            >
              下一页
            </button>
          </div>
        </section>

        <section class="panel notifications-viewer">
          <div class="notifications-viewer__header">
            <div>
              <p class="notifications-section-label">Sent Detail</p>
              <h2>{{ activeSentNotification ? '发送详情' : '等待选择记录' }}</h2>
            </div>
            <span v-if="activeSentNotification" class="notifications-badge">
              {{ sentReadSummary(sentStats).read }} / {{ sentReadSummary(sentStats).total }} 已读
            </span>
          </div>

          <div v-if="sentDetailLoading" class="notification-detail notification-detail--skeleton">
            <div class="notification-detail__body notification-detail__body--skeleton">
              <span class="skeleton-line skeleton-line--wide"></span>
              <span class="skeleton-line skeleton-line--medium"></span>
            </div>
          </div>

          <div v-else-if="activeSentNotification" class="notification-detail">
            <div class="notification-detail__hero">
              <div class="notification-detail__hero-copy">
                <span :class="['notification-chip notification-chip--detail', notificationTypeClass(activeSentNotification)]">
                  {{ activeSentNotification.type_word || '学员通知' }}
                </span>
                <h3>{{ activeSentNotification.title || '未命名通知' }}</h3>
                <p class="notification-detail__lead">
                  {{ activeSentNotification.content_preview || '这条通知暂无摘要内容。' }}
                </p>
              </div>

              <div class="notification-detail__hero-aside">
                <article class="detail-stat">
                  <span>接收人数</span>
                  <strong>{{ sentReadSummary(sentStats).total }}</strong>
                </article>
                <article class="detail-stat">
                  <span>已读 / 未读</span>
                  <strong>{{ sentReadSummary(sentStats).read }} / {{ sentReadSummary(sentStats).unread }}</strong>
                </article>
                <article class="detail-stat">
                  <span>发送时间</span>
                  <strong>{{ formatNotificationTime(activeSentNotification.send_at || activeSentNotification.created_at) }}</strong>
                </article>
              </div>
            </div>

            <div
              class="notification-detail__body"
              v-html="activeSentNotification.content || activeSentNotification.notification_content || '<p>暂无内容</p>'"
            ></div>

            <div class="sent-recipients">
              <h3>接收状态</h3>
              <div v-if="sentRecipients.length" class="sent-recipients__list">
                <article
                  v-for="item in sentRecipients"
                  :key="item.id || item.member_id"
                  class="sent-recipient"
                >
                  <strong>{{ item.name || item.real_name || item.username || item.member_name || '学员' }}</strong>
                  <span>{{ Number(item.is_read) === 1 ? '已读' : '未读' }}</span>
                </article>
              </div>
              <p v-else>暂无接收状态数据。</p>
            </div>
          </div>

          <div v-else class="notifications-empty notifications-empty--viewer">
            <strong>选择一条已发送通知</strong>
            <p>这里会展示阅读统计和接收人状态。</p>
          </div>
        </section>
      </div>
      </template>
    </section>
  </div>
</template>

<script>
import {
  fetchNotificationDetail,
  fetchNotificationList,
  fetchNotificationRecipientOptions,
  fetchSentNotificationList,
  fetchSentNotificationRecipients,
  fetchSentNotificationStats,
  markNotificationRead,
  sendStudentNotification
} from '@/api/notification'
import {
  decrementUnreadCount,
  loadUnreadCount,
  notificationState,
  setUnreadCount
} from '@/store/notification'

export default {
  name: 'NotificationsView',
  data() {
    return {
      loading: true,
      errorMessage: '',
      activeTab: 'inbox',
      notifications: [],
      total: 0,
      page: 1,
      pageSize: 15,
      readFilter: '',
      selectedId: 0,
      activeNotification: null,
      detailLoading: false,
      detailError: '',
      markingAll: false,
      recipientOptions: [],
      recipientKeyword: '',
      recipientLoading: false,
      sendingNotification: false,
      composeForm: {
        title: '',
        content: '',
        recipientIds: []
      },
      sentLoading: false,
      sentErrorMessage: '',
      sentNotifications: [],
      sentTotal: 0,
      sentPage: 1,
      sentPageSize: 15,
      selectedSentId: 0,
      activeSentNotification: null,
      sentDetailLoading: false,
      sentStats: null,
      sentRecipients: [],
      sentRecipientsTotal: 0,
      sentRecipientsPage: 1,
      sentRecipientsPageSize: 20
    }
  },
  computed: {
    tabOptions() {
      return [
        { label: '收件箱', value: 'inbox' },
        { label: '写通知', value: 'compose' },
        { label: '已发送', value: 'sent' }
      ]
    },
    readFilterOptions() {
      return [
        { label: '全部', value: '', description: '所有消息' },
        { label: '未读', value: 0, description: '待处理' },
        { label: '已读', value: 1, description: '已查看' }
      ]
    },
    isInitialLoading() {
      return this.loading && this.notifications.length === 0 && !this.errorMessage
    },
    unreadCount() {
      return notificationState.unreadCount
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize))
    },
    sentTotalPages() {
      return Math.max(1, Math.ceil(this.sentTotal / this.sentPageSize))
    },
    selectedRecipientOptions() {
      const selected = new Set(this.composeForm.recipientIds.map(id => Number(id)))
      return this.recipientOptions.filter(item => selected.has(Number(item.id)))
    }
  },
  created() {
    this.loadNotifications(true)
  },
  methods: {
    changeTab(value) {
      if (this.activeTab === value) return
      this.activeTab = value

      if (value === 'compose') {
        this.loadRecipientOptions()
        return
      }

      if (value === 'sent') {
        this.loadSentNotifications(true)
      }
    },
    async loadNotifications(shouldSelectFirst = false) {
      this.loading = true
      this.errorMessage = ''

      try {
        const result = await fetchNotificationList({
          page: this.page,
          page_size: this.pageSize,
          ...(this.readFilter !== '' ? { is_read: this.readFilter } : {})
        })

        this.notifications = result.list
        this.total = result.total
        this.page = result.page
        this.pageSize = result.page_size

        if (!this.notifications.length) {
          this.selectedId = 0
          this.activeNotification = null
          await loadUnreadCount()
          return
        }

        const nextSelected = shouldSelectFirst
          ? this.notifications[0]
          : this.notifications.find(item => item.id === this.selectedId) || this.notifications[0]

        if (nextSelected) {
          await this.handleSelect(nextSelected)
        }

        await loadUnreadCount()
      } catch (error) {
        this.errorMessage = error.message || '通知加载失败'
      } finally {
        this.loading = false
      }
    },
    async handleSelect(item) {
      if (!item || !item.id) return

      const wasUnread = Number(item.is_read) === 0

      this.selectedId = item.id
      this.detailLoading = true
      this.detailError = ''

      try {
        const detail = await fetchNotificationDetail(item.id)
        this.activeNotification = detail

        if (wasUnread) {
          item.is_read = 1
          item.is_read_word = '已读'
          decrementUnreadCount()
        }
      } catch (error) {
        this.detailError = error.message || '通知详情加载失败'
      } finally {
        this.detailLoading = false
      }
    },
    async handleMarkAllRead() {
      if (this.markingAll || this.unreadCount === 0) return

      this.markingAll = true

      try {
        await markNotificationRead()
        this.notifications = this.notifications.map(item => ({
          ...item,
          is_read: 1,
          is_read_word: '已读'
        }))

        if (this.activeNotification) {
          this.activeNotification = {
            ...this.activeNotification,
            is_read: 1
          }
        }

        setUnreadCount(0)

        if (this.readFilter === 0) {
          await this.loadNotifications(true)
        }
      } catch (error) {
        this.errorMessage = error.message || '批量标记已读失败'
      } finally {
        this.markingAll = false
      }
    },
    changePage(nextPage) {
      if (nextPage < 1 || nextPage > this.totalPages || nextPage === this.page) return
      this.page = nextPage
      this.loadNotifications(true)
    },
    changeReadFilter(value) {
      if (this.readFilter === value) return
      this.readFilter = value
      this.page = 1
      this.loadNotifications(true)
    },
    async loadRecipientOptions() {
      this.recipientLoading = true

      try {
        const list = await fetchNotificationRecipientOptions({
          keyword: this.recipientKeyword,
          page_size: 100
        })
        this.recipientOptions = list.map(item => this.normalizeRecipient(item)).filter(item => item.id > 0)
      } catch (error) {
        this.errorMessage = error.message || '接收人加载失败'
      } finally {
        this.recipientLoading = false
      }
    },
    normalizeRecipient(item) {
      const id = Number(item.id || item.member_id || item.mid || item.uid || 0)
      const name = item.name || item.real_name || item.realName || item.username || item.nickname || `学员 ${id}`
      const mobile = item.mobile || item.phone || item.tel || ''
      const courseName = item.course_name || item.courseName || item.class_name || item.group_name || ''

      return {
        ...item,
        id,
        name,
        mobile,
        courseName
      }
    },
    toggleRecipient(id) {
      const normalizedId = Number(id)
      const exists = this.composeForm.recipientIds.some(item => Number(item) === normalizedId)
      this.composeForm.recipientIds = exists
        ? this.composeForm.recipientIds.filter(item => Number(item) !== normalizedId)
        : this.composeForm.recipientIds.concat(normalizedId)
    },
    isRecipientSelected(id) {
      return this.composeForm.recipientIds.some(item => Number(item) === Number(id))
    },
    async handleSendStudentNotification() {
      if (this.sendingNotification) return
      if (!this.composeForm.title.trim()) {
        window.alert('请填写通知标题')
        return
      }
      if (!this.composeForm.content.trim()) {
        window.alert('请填写通知内容')
        return
      }
      if (!this.composeForm.recipientIds.length) {
        window.alert('请选择接收学员')
        return
      }

      this.sendingNotification = true

      try {
        await sendStudentNotification({
          title: this.composeForm.title.trim(),
          content: this.plainTextToHtml(this.composeForm.content),
          recipient_member_ids: this.composeForm.recipientIds
        })
        window.alert('通知已发送')
        this.composeForm = {
          title: '',
          content: '',
          recipientIds: []
        }
        this.sentPage = 1
        await this.loadSentNotifications(false)
        this.activeTab = 'sent'
      } catch (error) {
        window.alert(error.message || '通知发送失败')
      } finally {
        this.sendingNotification = false
      }
    },
    async loadSentNotifications(shouldSelectFirst = false) {
      this.sentLoading = true
      this.sentErrorMessage = ''

      try {
        const result = await fetchSentNotificationList({
          page: this.sentPage,
          page_size: this.sentPageSize
        })

        this.sentNotifications = result.list
        this.sentTotal = result.total
        this.sentPage = result.page
        this.sentPageSize = result.page_size

        if (!this.sentNotifications.length) {
          this.selectedSentId = 0
          this.activeSentNotification = null
          this.sentStats = null
          this.sentRecipients = []
          this.sentRecipientsTotal = 0
          return
        }

        const nextSelected = shouldSelectFirst
          ? this.sentNotifications[0]
          : this.sentNotifications.find(item => item.id === this.selectedSentId) || this.sentNotifications[0]

        if (nextSelected) {
          await this.handleSelectSent(nextSelected)
        }
      } catch (error) {
        this.sentErrorMessage = error.message || '已发送通知加载失败'
      } finally {
        this.sentLoading = false
      }
    },
    async handleSelectSent(item) {
      if (!item || !item.id) return

      this.selectedSentId = item.id
      this.activeSentNotification = item
      this.sentDetailLoading = true

      try {
        const [stats, recipients] = await Promise.all([
          fetchSentNotificationStats(item.id),
          fetchSentNotificationRecipients(item.id, {
            page: this.sentRecipientsPage,
            page_size: this.sentRecipientsPageSize
          })
        ])
        this.sentStats = stats
        this.sentRecipients = recipients.list
        this.sentRecipientsTotal = recipients.total
        this.sentRecipientsPage = recipients.page
        this.sentRecipientsPageSize = recipients.page_size
      } catch (error) {
        this.sentStats = null
        this.sentRecipients = []
        this.sentRecipientsTotal = 0
        this.sentErrorMessage = error.message || '通知统计加载失败'
      } finally {
        this.sentDetailLoading = false
      }
    },
    changeSentPage(nextPage) {
      if (nextPage < 1 || nextPage > this.sentTotalPages || nextPage === this.sentPage) return
      this.sentPage = nextPage
      this.loadSentNotifications(true)
    },
    plainTextToHtml(value) {
      return String(value || '')
        .split(/\n{2,}/)
        .map(part => `<p>${this.escapeHtml(part).replace(/\n/g, '<br>')}</p>`)
        .join('')
    },
    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    sentReadSummary(stats) {
      const source = stats || {}
      const total = Number(source.total || source.recipient_total || source.receiver_total || 0)
      const read = Number(source.read_count || source.read_total || 0)
      const unread = Number(source.unread_count || Math.max(0, total - read) || 0)
      return { total, read, unread }
    },
    notificationInitial(item) {
      const source = item.type_word || item.title || 'N'
      return String(source).slice(0, 1).toUpperCase()
    },
    notificationTypeClass(item) {
      const type = String(item.type_word || '').toLowerCase()
      if (type.includes('考试')) return 'is-exam'
      if (type.includes('课程')) return 'is-course'
      if (type.includes('作业')) return 'is-task'
      return 'is-system'
    },
    formatNotificationTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value

      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${month}-${day} ${hours}:${minutes}`
    },
    formatRelativeTime(value) {
      if (!value) return '时间未知'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '刚刚同步'

      const diff = Date.now() - date.getTime()
      const minute = 60 * 1000
      const hour = 60 * minute
      const day = 24 * hour

      if (diff < hour) {
        return `${Math.max(1, Math.floor(diff / minute))} 分钟前`
      }

      if (diff < day) {
        return `${Math.floor(diff / hour)} 小时前`
      }

      return `${Math.floor(diff / day)} 天前`
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/tokens';

.notifications-page {
  position: relative;
}

.notifications-page::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 360px;
  background:
    radial-gradient(circle at top left, rgba($color-accent, 0.18), transparent 42%),
    linear-gradient(180deg, #fff6ef 0%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
}

.notifications-page .container {
  position: relative;
  z-index: 1;
}

.notifications-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) 340px;
  gap: 24px;
  align-items: stretch;
}

.notifications-hero__loading {
  display: contents;
}

.notifications-hero__main,
.hero-loading__main {
  min-width: 0;
  padding: 36px;
  border-radius: 32px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 249, 244, 0.98)),
    $color-surface;
  border: 1px solid rgba($color-primary, 0.1);
  box-shadow: 0 22px 50px rgba($color-primary, 0.12);
  display: grid;
  gap: 28px;
  position: relative;
  overflow: hidden;
}

.notifications-hero__main::before,
.hero-loading__main::before {
  content: '';
  position: absolute;
  inset: -20% auto auto 60%;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba($color-accent, 0.2), transparent 70%);
  filter: blur(8px);
}

.notifications-hero__intro {
  display: grid;
  gap: 18px;
  position: relative;
  z-index: 1;
}

.notifications-hero__signal {
  display: inline-grid;
  grid-template-columns: auto 1fr;
  gap: 4px 12px;
  align-items: center;
  padding: 16px 18px;
  border-radius: 20px;
  width: fit-content;
  min-width: min(100%, 360px);
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba($color-primary, 0.08);
  box-shadow: $shadow-sm;
}

.notifications-hero__signal-dot {
  grid-row: 1 / span 2;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: $color-success;
  box-shadow: 0 0 0 8px rgba($color-success, 0.12);
}

.notifications-hero__signal strong {
  font-size: 15px;
  color: $color-text-primary;
}

.notifications-hero__signal span:last-child {
  color: $color-text-secondary;
  font-size: 13px;
}

.notifications-hero__metrics,
.hero-loading__metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  position: relative;
  z-index: 1;
}

.hero-metric {
  padding: 22px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba($color-primary, 0.08);
  box-shadow: $shadow-sm;
  display: grid;
  gap: 8px;
}

.hero-metric span {
  font-size: 13px;
  color: $color-text-tertiary;
  letter-spacing: 0.03em;
}

.hero-metric strong {
  font-size: clamp(28px, 4vw, 36px);
  line-height: 1;
  color: $color-text-primary;
}

.hero-metric small {
  font-size: 13px;
  color: $color-text-secondary;
}

.hero-metric--skeleton {
  gap: 12px;
}

.notifications-command,
.hero-loading__aside {
  position: relative;
  min-width: 0;
}

.notifications-command__card,
.hero-loading__aside {
  height: 100%;
  padding: 28px;
  border-radius: 32px;
  background: linear-gradient(160deg, #1f2027 0%, #34374a 100%);
  color: white;
  box-shadow: 0 24px 48px rgba(31, 32, 39, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: grid;
  align-content: start;
  gap: 18px;
  overflow: hidden;
  position: relative;
}

.notifications-command__card::before,
.hero-loading__aside::before {
  content: '';
  position: absolute;
  inset: auto -40px -50px auto;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba($color-accent, 0.36), transparent 68%);
}

.notifications-command__orbit {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.12);
  pointer-events: none;
}

.notifications-command__orbit--lg {
  width: 220px;
  height: 220px;
  right: -92px;
  top: -72px;
}

.notifications-command__orbit--sm {
  width: 120px;
  height: 120px;
  right: 28px;
  bottom: 28px;
}

.notifications-section-label {
  font-size: 12px;
  line-height: 1;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba($color-text-secondary, 0.9);
}

.notifications-command .notifications-section-label {
  color: rgba(255, 255, 255, 0.68);
}

.notifications-command__card h2 {
  font-size: 28px;
  line-height: 1.25;
  position: relative;
  z-index: 1;
}

.notifications-command__copy {
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.7;
  position: relative;
  z-index: 1;
}

.notifications-command__meta {
  display: grid;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.command-pill {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
}

.command-pill span {
  color: rgba(255, 255, 255, 0.62);
  font-size: 13px;
}

.command-pill strong {
  font-size: 14px;
}

.notifications-command__actions,
.hero-loading__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.notifications-status {
  margin-top: 20px;
}

.notifications-tabs {
  width: fit-content;
  max-width: 100%;
  margin-top: 24px;
  padding: 6px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba($color-primary, 0.1);
  box-shadow: $shadow-sm;
  display: flex;
  gap: 6px;
  overflow-x: auto;
}

.notifications-tab {
  min-width: 88px;
  min-height: 38px;
  padding: 0 16px;
  border-radius: 14px;
  color: $color-text-secondary;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.notifications-tab:hover,
.notifications-tab.is-active {
  color: white;
  background: $gradient-primary;
  box-shadow: 0 10px 22px rgba($color-primary, 0.18);
}

.notifications-workspace {
  display: grid;
  grid-template-columns: 380px minmax(0, 1fr);
  gap: 24px;
  margin-top: 28px;
  align-items: start;
}

.notifications-sidebar,
.notifications-viewer {
  border-radius: 30px;
  border-color: rgba($color-primary, 0.08);
  box-shadow: 0 16px 40px rgba($color-primary, 0.08);
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(12px);
}

.notifications-sidebar__header,
.notifications-viewer__header {
  padding: 28px 28px 18px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.notifications-sidebar__header h2,
.notifications-viewer__header h2 {
  font-size: 28px;
  margin-top: 6px;
}

.notifications-badge {
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba($color-primary, 0.08);
  color: $color-primary;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.notifications-sidebar__toolbar {
  margin: 0 28px 20px;
  padding: 14px;
  border-radius: 22px;
  background: linear-gradient(135deg, #fff8f3 0%, #ffffff 100%);
  border: 1px solid rgba($color-primary, 0.08);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(118px, 1fr));
  gap: 12px;
}

.notifications-sidebar__toolbar--skeleton {
  align-items: center;
}

.toolbar-chip {
  padding: 12px 14px;
  border-radius: 18px;
  background: white;
  border: 1px solid rgba($color-primary, 0.08);
  display: grid;
  gap: 4px;
}

.toolbar-chip--button {
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
}

.toolbar-chip--button:hover,
.toolbar-chip--button.is-active {
  border-color: rgba($color-primary, 0.36);
  background: rgba($color-primary-light, 0.42);
}

.toolbar-chip--button:disabled {
  cursor: not-allowed;
  opacity: 0.68;
}

.toolbar-chip span {
  font-size: 12px;
  color: $color-text-tertiary;
}

.toolbar-chip strong {
  font-size: 14px;
  color: $color-text-primary;
}

.notifications-list {
  padding: 0 16px 16px;
  display: grid;
  gap: 10px;
}

.notification-card {
  width: 100%;
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: 12px;
  padding: 14px;
  border-radius: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #fcfcfc 100%);
  border: 1px solid transparent;
  box-shadow: inset 0 0 0 1px rgba($color-border, 0.6);
  text-align: left;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, background 0.22s ease;
}

.notification-card:hover {
  transform: translateY(-2px);
  border-color: rgba($color-primary, 0.14);
  box-shadow: 0 12px 28px rgba($color-primary, 0.12);
}

.notification-card.is-active {
  background: linear-gradient(180deg, #fff8f2 0%, #ffffff 100%);
  border-color: rgba($color-primary, 0.24);
  box-shadow: 0 18px 32px rgba($color-primary, 0.16);
}

.notification-card.is-unread {
  box-shadow: inset 0 0 0 1px rgba($color-primary, 0.12);
}

.notification-card--skeleton {
  cursor: default;
}

.notification-card__avatar {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: $gradient-primary;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 16px;
  box-shadow: 0 12px 24px rgba($color-primary, 0.24);
}

.notification-card__avatar--skeleton {
  background: linear-gradient(90deg, #eef1f4 0%, #f7f8fa 50%, #eef1f4 100%);
  box-shadow: none;
}

.notification-card__content {
  min-width: 0;
  display: grid;
  gap: 8px;
}

.notification-card__topline,
.notification-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.notification-card__time,
.notification-card__relative {
  font-size: 12px;
  color: $color-text-tertiary;
}

.notification-card h3 {
  margin: 0;
  font-size: 15px;
  line-height: 1.4;
  color: $color-text-primary;
}

.notification-card p {
  margin: 0;
  color: $color-text-secondary;
  font-size: 13px;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-chip,
.notification-state {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  min-height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.notification-chip {
  background: rgba($color-primary, 0.08);
  color: $color-primary;
}

.notification-chip.is-system {
  background: rgba($color-info, 0.1);
  color: darken($color-info, 6%);
}

.notification-chip.is-course {
  background: rgba($color-success, 0.12);
  color: darken($color-success, 6%);
}

.notification-chip.is-exam {
  background: rgba($color-warning, 0.14);
  color: darken($color-warning, 10%);
}

.notification-chip.is-task {
  background: rgba($color-danger, 0.12);
  color: darken($color-danger, 4%);
}

.notification-chip--detail {
  min-height: 32px;
  padding: 0 14px;
  font-size: 13px;
}

.notification-state {
  background: rgba($color-text-primary, 0.06);
  color: $color-text-secondary;
}

.notification-state.is-unread {
  background: rgba($color-primary, 0.12);
  color: $color-primary;
}

.notifications-pagination {
  padding: 4px 28px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.pagination-info {
  color: $color-text-secondary;
  font-size: 14px;
}

.notification-compose {
  padding: 0 28px 28px;
  display: grid;
  gap: 20px;
}

.notification-field {
  display: grid;
  gap: 10px;
}

.notification-field--compact {
  gap: 6px;
}

.notification-field span,
.notification-compose__selected > span {
  font-size: 13px;
  color: $color-text-tertiary;
  font-weight: 700;
}

.notification-field input,
.notification-field textarea {
  width: 100%;
  border: 1px solid rgba($color-primary, 0.12);
  border-radius: 18px;
  background: white;
  color: $color-text-primary;
  font-size: 15px;
  line-height: 1.6;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.notification-field input {
  min-height: 46px;
  padding: 0 16px;
}

.notification-field textarea {
  min-height: 260px;
  padding: 16px;
  resize: vertical;
}

.notification-field input:focus,
.notification-field textarea:focus {
  border-color: rgba($color-primary, 0.42);
  box-shadow: 0 0 0 4px rgba($color-primary, 0.08);
}

.notification-compose__selected {
  padding: 18px;
  border-radius: 20px;
  border: 1px dashed rgba($color-primary, 0.18);
  background: linear-gradient(180deg, #fff8f3 0%, #ffffff 100%);
  display: grid;
  gap: 12px;
}

.notification-compose__selected p {
  margin: 0;
  color: $color-text-secondary;
  font-size: 14px;
}

.recipient-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.recipient-pill {
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba($color-primary, 0.1);
  color: $color-primary;
  font-size: 13px;
  font-weight: 700;
}

.notification-compose__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.sent-recipients {
  padding: 24px;
  border-radius: 28px;
  background: white;
  border: 1px solid rgba($color-primary, 0.08);
  display: grid;
  gap: 16px;
}

.sent-recipients h3 {
  margin: 0;
  font-size: 20px;
}

.sent-recipients p {
  margin: 0;
  color: $color-text-secondary;
}

.sent-recipients__list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.sent-recipient {
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba($color-primary, 0.08);
  background: linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.sent-recipient strong {
  min-width: 0;
  color: $color-text-primary;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sent-recipient span {
  flex: 0 0 auto;
  color: $color-primary;
  font-size: 13px;
  font-weight: 700;
}

.notification-detail {
  padding: 0 28px 28px;
  display: grid;
  gap: 24px;
}

.notification-detail__hero {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) 260px;
  gap: 18px;
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba($color-primary, 0.07), rgba($color-accent, 0.08)),
    white;
  border: 1px solid rgba($color-primary, 0.1);
}

.notification-detail__hero--skeleton {
  background: linear-gradient(180deg, #fafbfc 0%, #ffffff 100%);
}

.notification-detail__hero-copy {
  display: grid;
  gap: 14px;
  align-content: start;
}

.notification-detail__hero-copy h3 {
  font-size: clamp(28px, 4vw, 36px);
  line-height: 1.24;
  margin: 0;
}

.notification-detail__lead {
  color: $color-text-secondary;
  line-height: 1.8;
}

.notification-detail__hero-aside {
  display: grid;
  gap: 12px;
}

.detail-stat {
  padding: 16px 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba($color-primary, 0.08);
  box-shadow: $shadow-xs;
  display: grid;
  gap: 6px;
}

.detail-stat span {
  font-size: 12px;
  color: $color-text-tertiary;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.detail-stat strong {
  font-size: 15px;
  line-height: 1.5;
  color: $color-text-primary;
}

.notification-detail__body {
  min-height: 360px;
  padding: 28px;
  border-radius: 28px;
  background: white;
  border: 1px solid rgba($color-primary, 0.08);
  line-height: 1.95;
  font-size: 15px;
  color: $color-text-primary;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.notification-detail__body--skeleton {
  min-height: 300px;
  display: grid;
  align-content: start;
  gap: 14px;
}

.notification-detail__body ::v-deep img {
  max-width: 100%;
  border-radius: 20px;
  margin: 16px 0;
}

.notification-detail__body ::v-deep p + p {
  margin-top: 16px;
}

.notifications-empty {
  padding: 72px 28px;
  text-align: center;
}

.notifications-empty--sidebar {
  margin: 0 16px 16px;
  border-radius: 26px;
  background: linear-gradient(180deg, #fff8f3 0%, #ffffff 100%);
  border: 1px dashed rgba($color-primary, 0.18);
}

.notifications-empty--viewer {
  min-height: 520px;
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 8px;
}

.notifications-empty__art {
  font-size: 52px;
  margin-bottom: 10px;
}

.notifications-empty strong {
  font-size: 20px;
}

.notifications-empty p {
  color: $color-text-secondary;
}

.skeleton-line,
.skeleton-chip,
.skeleton-badge {
  position: relative;
  overflow: hidden;
  display: block;
  background: #eef1f4;
  border-radius: 999px;
}

.skeleton-line::after,
.skeleton-chip::after,
.skeleton-badge::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.72), transparent);
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-line {
  height: 12px;
}

.skeleton-line--mini {
  width: 72px;
}

.skeleton-line--short {
  width: 120px;
}

.skeleton-line--medium {
  width: 68%;
}

.skeleton-line--wide {
  width: 100%;
}

.skeleton-line--title {
  width: 52%;
  height: 38px;
  border-radius: 18px;
}

.skeleton-line--section-title {
  width: 140px;
  height: 28px;
  border-radius: 14px;
  margin-top: 10px;
}

.skeleton-line--stat {
  width: 64px;
  height: 28px;
  border-radius: 14px;
}

.skeleton-line--card-title {
  width: 74%;
  height: 20px;
  border-radius: 10px;
}

.skeleton-chip {
  width: 88px;
  height: 28px;
}

.skeleton-chip--small {
  width: 70px;
  height: 24px;
}

.skeleton-chip--primary {
  width: 124px;
}

.skeleton-badge {
  width: 86px;
  height: 34px;
}

@keyframes skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}

@media (max-width: 1180px) {
  .notifications-hero,
  .notifications-workspace {
    grid-template-columns: 1fr;
  }

  .notifications-command,
  .hero-loading__aside {
    min-height: 260px;
  }
}

@media (max-width: 900px) {
  .notifications-hero__metrics,
  .hero-loading__metrics,
  .notifications-sidebar__toolbar,
  .notification-detail__hero {
    grid-template-columns: 1fr;
  }

  .notifications-hero__main,
  .hero-loading__main,
  .notifications-command__card,
  .hero-loading__aside,
  .notifications-sidebar__header,
  .notifications-viewer__header,
  .notification-detail,
  .notification-detail__body {
    padding-left: 22px;
    padding-right: 22px;
  }

  .notification-detail {
    padding-bottom: 22px;
  }
}

@media (max-width: 640px) {
  .page-shell.notifications-page {
    padding-top: 12px;
  }

  .notifications-hero__main,
  .hero-loading__main,
  .notifications-command__card,
  .hero-loading__aside {
    padding: 24px;
    border-radius: 26px;
  }

  .notifications-sidebar__header,
  .notifications-viewer__header {
    padding: 22px 22px 18px;
  }

  .notifications-sidebar__toolbar,
  .notifications-pagination,
  .notification-compose,
  .notification-detail {
    margin-left: 0;
    margin-right: 0;
    padding-left: 22px;
    padding-right: 22px;
  }

  .notifications-list {
    padding: 0 12px 12px;
  }

  .notification-card {
    grid-template-columns: 1fr;
  }

  .notification-card__avatar {
    width: 42px;
    height: 42px;
  }

  .notifications-pagination {
    flex-direction: column;
    align-items: stretch;
  }

  .notifications-tabs {
    width: 100%;
  }

  .notifications-tab {
    flex: 1 0 auto;
  }

  .notifications-pagination .btn,
  .notification-compose__actions .btn {
    width: 100%;
  }

  .notification-compose__actions {
    flex-direction: column;
  }

  .notification-detail__body {
    min-height: 260px;
    padding: 22px;
  }

  .notifications-empty--viewer {
    min-height: 360px;
  }
}
</style>
